import type {
    CheckoutError,
    Component,
    PaymentSessionResponse,
    PayPaymentSessionSuccessfulResponse,
} from '@checkout.com/checkout-web-components';
import { CardScheme, loadCheckoutWebComponents } from '@checkout.com/checkout-web-components';
import { create } from 'zustand';

import { DEBIT_DEPOSIT_AMOUNT, DEBIT_DEPOSIT_FEE } from 'constants/session';
import * as metrics from 'lib/metrics';
import { useAuthStore } from 'lib/stores/authStore';
import { selectIsDark, useColorPaletteOverrideStore } from 'lib/stores/colorPaletteOverrideStore';
import type { ServerError } from 'models';
import type {
    CreateStripeCryptoWithdrawalResponse,
    CreateStripeWithdrawalResponse,
    CreateWithdrawalResponse,
    DebitCardPaymentSession,
    DepositDestination,
    InitiateBraintreeDepositRequest,
    InitiateBraintreeWithdrawalResponse,
    SavedDebitCardDepositRequest,
    SavedDebitCardDepositResponse,
    StripeAccountLinkResponse,
    StripeANIDepositInstrument,
    StripeANIDepositResponse,
    StripePaymentIntent,
    StripePaymentIntentRequest,
    WithdrawalSource,
} from 'models/reqres';
import { useDepositDialogStore } from 'stores/zustand/depositDialogStore';
import {
    createStripeANIDeposit as createStripeANIDepositRequest,
    createStripeCashappPaymentIntent as createStripeCashappPaymentIntentRequest,
    createStripeCryptoPaymentIntent as createStripeCryptoPaymentIntentRequest,
    createStripeCryptoWithdrawal as createStripeCryptoWithdrawalRequest,
    createStripePaymentIntent as createStripePaymentIntentRequest,
    createStripeWithdrawal as createStripeWithdrawalRequest,
    debitCardWithdrawal as debitCardWithdrawalRequest,
    getBraintreeClientToken as getBraintreeClientTokenRequest,
    getDebitPaymentStatus as getDebitPaymentStatusRequest,
    getStripeAccountLink as getStripeAccountLinkRequest,
    initiateBraintreeDeposit as initiateBraintreeDepositRequest,
    initiateBraintreeWithdrawal as initiateBraintreeWithdrawalRequest,
    savedDebitCardDeposit as savedDebitCardDepositRequest,
    startDebitCardPaymentSession as startDebitCardPaymentSessionRequest,
} from 'transport/domains/payments';
import { toMoneyWithCentsIfCents } from 'utils/caseing';
import {
    buildCheckoutFailureUrl,
    CHECKOUT_3DS_FAILURE_DESCRIPTION,
    CHECKOUT_3DS_FAILURE_TITLE,
    CHECKOUT_3DS_REJECTED_CODE,
    getTrustedCheckout3DSUrl,
} from 'utils/checkoutRedirect';
import config from 'utils/config';
import { getDepositRequestTrackingProps } from 'utils/depositTracking';
import logProviderFactory from 'utils/logger/logProviderFactory';
import { noop } from 'utils/noop';
import type { PaymentStatus } from 'utils/payments';
import { getCheckoutTemplate, getCheckoutTranslations } from 'utils/payments';
import { getRiskifiedSessionId } from 'utils/riskified';
import { fetchSardineSessionKey } from 'utils/sardine';
import { setLocalStorageWithExpiration } from 'utils/storage';

import { usePaymentsWalletStore } from './paymentsWalletStore';

const CENTS_PER_DOLLAR = 100;
const CENTICENTS_PER_DOLLAR = 10000;

// Invalidated by dispose(), which resetAppStateOnLogout calls on every session end.
// Mirrors cryptoFlowStore's flowGeneration: without it, a response that lands after a
// teardown writes this store for whoever signed in next — stripePaymentIntent alone
// carries the previous user's transfer_id, payment_intent_id and deposit_id.
let flowGeneration = 0;

/**
 * Two clocks, and both have to be checked before a request goes out. `!sardine.ok`
 * reports a *session* teardown only (resetSardineState, from endSession /
 * applyForcedDisconnectTeardown). dispose() also runs on the unmount teardown —
 * resetAppState(false), when AppEffectsBootstrap unmounts on navigation to a
 * provider-less route — which bumps flowGeneration and leaves Sardine untouched. A
 * request issued in that window has every one of its callbacks suppressed as stale, so
 * the transfer completes with no success or error handling at all. Hence the
 * `generation !== flowGeneration` half of each pre-request check below.
 *
 * Wraps a request callback so it no-ops once the session that issued the request has
 * ended. Applied to success, error and finally callbacks alike: the success path writes
 * payment intents and sessions into the store, the error path drives snackbars and
 * dialog state, and the finally path clears loading flags dispose() has already reset.
 *
 * Capture `generation` before issuing the request — before any await, since the Sardine
 * fetch is itself a teardown window — then wrap every callback handed to the transport.
 */
function scopedToSession<A extends unknown[]>(generation: number, fn: (...args: A) => void): (...args: A) => void {
    return (...args: A) => {
        if (generation !== flowGeneration) return;
        fn(...args);
    };
}

function getCheckoutFailureUrl(): string {
    const baseUrl = typeof window === 'undefined' ? config.baseUrl : window.location.origin;
    return buildCheckoutFailureUrl(baseUrl);
}

// Checkout Web Components loads a remote script that can transiently fail to
// load (timeout or script_failed). Retry a couple of times with a short backoff
// before giving up, so a CDN/network blip doesn't fail the whole deposit.
const CHECKOUT_LOADER_RETRY_BACKOFFS_MS = [500, 1000];
const RETRYABLE_CHECKOUT_LOADER_ERROR_CODES = ['script_timed_out', 'script_failed'];

function wait(ms: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

function isRetryableCheckoutLoaderError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
        return false;
    }
    const candidate = error as { code?: unknown };
    return typeof candidate.code === 'string' && RETRYABLE_CHECKOUT_LOADER_ERROR_CODES.includes(candidate.code);
}

async function loadCheckoutWebComponentsWithRetry(
    options: Parameters<typeof loadCheckoutWebComponents>[0],
): Promise<Awaited<ReturnType<typeof loadCheckoutWebComponents>>> {
    for (let attempt = 0; ; attempt += 1) {
        try {
            return await loadCheckoutWebComponents(options);
        } catch (error) {
            const isLastAttempt = attempt >= CHECKOUT_LOADER_RETRY_BACKOFFS_MS.length;
            if (isLastAttempt || !isRetryableCheckoutLoaderError(error)) {
                throw error;
            }
            logProviderFactory.logMessage(
                `Checkout web components load failed transiently, retrying (attempt ${attempt + 1} of ${CHECKOUT_LOADER_RETRY_BACKOFFS_MS.length})`,
            );
            await wait(CHECKOUT_LOADER_RETRY_BACKOFFS_MS[attempt]);
        }
    }
}

function buildTokenizedDepositPayload(
    amountInCenticents: number,
    token: string,
    successUrl: string,
    failureUrl: string,
    addressInfo: {
        addressLineOne?: string;
        addressLineTwo?: string;
        city?: string;
        state?: string;
        zipCode?: string;
        country?: string;
        saveCard?: boolean;
    },
    destination?: DepositDestination,
    sardineSessionKey?: string,
): SavedDebitCardDepositRequest {
    const payload: SavedDebitCardDepositRequest = {
        amount: amountInCenticents,
        token,
        success_url: successUrl,
        failure_url: failureUrl,
        destination,
        sardine_session_key: sardineSessionKey || undefined,
        ...getDepositRequestTrackingProps(),
    };

    if (addressInfo.addressLineOne) payload.address_line_one = addressInfo.addressLineOne;
    if (addressInfo.addressLineTwo) payload.address_line_two = addressInfo.addressLineTwo;
    if (addressInfo.city) payload.city = addressInfo.city;
    if (addressInfo.state) payload.state = addressInfo.state;
    if (addressInfo.zipCode) payload.zip_code = addressInfo.zipCode;
    if (addressInfo.country) payload.country = addressInfo.country;

    // Default to saving card for tokenized payments
    payload.save_card = addressInfo.saveCard !== false;

    return payload;
}

function getCardWalletType(isApplePay: boolean, isGooglePay: boolean): StripePaymentIntentRequest['card_wallet_type'] {
    if (isApplePay) return 'applepay';
    if (isGooglePay) return 'googlepay';
    return undefined;
}

// Ephemeral deposit/withdrawal flow state. Holds paymentStatus (the deposit
// result status the Stripe/Checkout flows set and the route logic reads),
// the Stripe payment-intent/session state, and the Braintree
// client-token/deposit/withdrawal flow.
interface PaymentsFlowState {
    paymentStatus: PaymentStatus;

    // Generic in-flight flag for Stripe flows. Account-link creation also uses
    // it to reject duplicate clicks while the first vendor request is pending.
    loading: boolean;
    loadingProviderPayment: boolean;
    loadingDebitCardWithdrawal: boolean;
    loadedDebitCardWithdrawal: boolean;
    loadingDebitCardDeposit: boolean;
    loadedDebitCardDeposit: boolean;
    // Latest Stripe payment intent (client secret + ids) for the deposit UI.
    stripePaymentIntent: StripePaymentIntent | undefined;

    // Checkout.com debit-card flow
    paymentSession: DebitCardPaymentSession | undefined;
    flowComponent: Component | undefined;
    loadedFlow: boolean;
    // Sardine session key minted by startPaymentSession. fetchSardineSessionKey
    // consumes the dialog's prestarted session, so the tokenized deposit that
    // follows would otherwise refetch a fresh session that has collected none of
    // the user's card-entry behavior. Held here so both requests score against
    // the same session.
    paymentSessionSardineKey: string;

    // Braintree deposit/withdrawal flow
    braintreeClientToken: string | undefined;
    braintreeClientMetadataId: string | undefined;
    loadedBraintreeClientToken: boolean;
    loadingBraintree: boolean;

    actions: {
        setPaymentStatus: (status: PaymentStatus) => void;
        setStripePaymentIntent: (paymentIntent: StripePaymentIntent | undefined) => void;
        setPaymentSession: (paymentSession: DebitCardPaymentSession | undefined) => void;
        setLoadedFlow: (loaded: boolean) => void;
        setLoadingProviderPayment: (loading: boolean) => void;
        // Reset all flow state to defaults on session teardown (logout / forced
        // disconnect), so tokens and in-flight flow state never bleed into the
        // next session (potentially a different user). Wired into resetAppStateOnLogout().
        dispose: () => void;
        createStripePaymentIntent: (
            userId: string,
            amountInDollars: number,
            onSuc?: () => void,
            onErr?: (error: ServerError) => void,
            debitCardId?: string,
            isCashApp?: boolean,
            destination?: DepositDestination,
        ) => Promise<void>;
        createStripeANIDeposit: (
            userId: string,
            amountInDollars: number,
            instrument: StripeANIDepositInstrument,
            onSuc?: (response: StripeANIDepositResponse) => void,
            onErr?: (error: ServerError) => void,
            destination?: DepositDestination,
        ) => Promise<void>;
        createStripeCryptoPaymentIntent: (
            userId: string,
            amountInDollars: number,
            context: {
                destination?: DepositDestination;
                depositFlowId?: string;
                frontendSessionId?: string;
                returnUrl?: string;
            },
            onSuc?: (clientSecret: string, depositId: string | undefined) => void,
            onErr?: (error: ServerError) => void,
        ) => Promise<void>;
        getBraintreeClientToken: (userId: string, onSuc?: () => void, onErr?: () => void) => void;
        initiateBraintreeDeposit: (
            userId: string,
            amountInDollars: number,
            type: 'paypal' | 'venmo',
            paymentMethodNonce?: string,
            paymentMethodId?: string,
            deviceData?: string,
            onSuc?: (transactionId: string, transferId?: string) => void,
            onErr?: (error: ServerError) => void,
            destination?: DepositDestination,
        ) => Promise<void>;
        initiateBraintreeWithdrawal: (
            userId: string,
            amountInCents: number,
            type: 'paypal' | 'venmo',
            mfaCode: string,
            onSuc?: (response: InitiateBraintreeWithdrawalResponse) => void,
            onErr?: (error: ServerError) => void,
            source?: WithdrawalSource,
        ) => Promise<void>;
        clearBraintreeData: () => void;
        debitCardWithdrawal: (
            userId: string,
            amountInCents: number,
            mfaCode: string,
            paymentId?: string,
            debitCardId?: string,
            onSuc?: (response?: CreateWithdrawalResponse) => void,
            onErr?: (error: ServerError) => void,
            source?: WithdrawalSource,
        ) => Promise<void>;
        getStripeAccountLink: (
            userId: string,
            returnUrl: string,
            refreshUrl: string,
            onSuc?: (response: StripeAccountLinkResponse) => void,
            onErr?: (error: ServerError) => void,
        ) => void;
        createStripeWithdrawal: (
            userId: string,
            amountInCents: number,
            mfaCode: string,
            onSuc?: (response: CreateStripeWithdrawalResponse) => void,
            onErr?: (err: ServerError) => void,
            source?: WithdrawalSource,
            payoutMethodId?: string,
        ) => Promise<void>;
        createStripeCryptoWithdrawal: (
            userId: string,
            amountInCents: number,
            payoutMethodId: string,
            mfaCode: string,
            onSuc?: (response: CreateStripeCryptoWithdrawalResponse) => void,
            onErr?: (err: ServerError) => void,
            source?: WithdrawalSource,
        ) => Promise<void>;
        tokenizedCardDeposit: (
            userId: string,
            amountInDollars: number,
            token: string,
            pathname: string,
            addressLineOne?: string,
            addressLineTwo?: string,
            city?: string,
            state?: string,
            zipCode?: string,
            country?: string,
            saveCard?: boolean,
            onSuc?: (response: SavedDebitCardDepositResponse) => void,
            onErr?: (error: ServerError) => void,
            destination?: DepositDestination,
        ) => Promise<void>;
        tokenizedCardWithdrawal: (
            userId: string,
            amountInCents: number,
            mfaCode: string,
            token: string,
            onSuc?: () => void,
            onErr?: (error: ServerError) => void,
            source?: WithdrawalSource,
        ) => Promise<void>;
        savedDebitCardDeposit: (
            userId: string,
            amountInDollars: number,
            feeInDollars: number,
            debitCardId: string,
            pathname: string,
            destination: DepositDestination,
            onSuc?: (response?: SavedDebitCardDepositResponse) => void,
            onErr?: (error?: ServerError, message?: string, details?: string, code?: string) => void,
        ) => Promise<void>;
        getDebitPaymentStatus: (
            userId: string,
            checkoutPaymentId: string,
            onSuc?: (title: string, reason: string) => void,
            onErr?: () => void,
        ) => void;
        startPaymentSession: (
            userId: string,
            amountInDollars: number,
            pathname: string,
            addressLineOne: string,
            addressLineTwo: string | undefined,
            city: string,
            state: string,
            zipCode: string,
            country: string,
            saveCard?: boolean,
            onSuc?: () => void,
            onErr?: (error: ServerError) => void,
            destination?: DepositDestination,
        ) => Promise<void>;
        createCheckoutFlow: (
            operation: 'deposit' | 'withdrawal',
            amountInDollars: number,
            feeInDollars: number,
            onReady: () => void,
            onPaymentCompleted: (paymentResponse: PayPaymentSessionSuccessfulResponse) => void,
            // serverError is set when the failure came from the Kalshi tokenized-deposit call
            // rather than the Checkout.com SDK, so callers can show the backend's own copy.
            onErr: (error?: CheckoutError, serverError?: ServerError) => void,
            addressLineOne?: string,
            addressLineTwo?: string,
            city?: string,
            state?: string,
            zipCode?: string,
            country?: string,
            saveCard?: boolean,
            mfaCode?: string,
            destination?: DepositDestination,
            onPaymentStarted?: () => void,
        ) => Promise<void>;
    };
}

// Default flow state — also the reset target on session teardown (see dispose).
const INITIAL_FLOW_STATE: Omit<PaymentsFlowState, 'actions'> = {
    paymentStatus: 'pending',
    loading: false,
    loadingProviderPayment: false,
    loadingDebitCardWithdrawal: false,
    loadedDebitCardWithdrawal: false,
    loadingDebitCardDeposit: false,
    loadedDebitCardDeposit: false,
    stripePaymentIntent: undefined,
    paymentSession: undefined,
    flowComponent: undefined,
    loadedFlow: false,
    paymentSessionSardineKey: '',
    braintreeClientToken: undefined,
    braintreeClientMetadataId: undefined,
    loadedBraintreeClientToken: false,
    loadingBraintree: false,
};

export const usePaymentsFlowStore = create<PaymentsFlowState>()((set, get) => ({
    ...INITIAL_FLOW_STATE,

    actions: {
        setPaymentStatus: (paymentStatus) => set({ paymentStatus }),
        setStripePaymentIntent: (stripePaymentIntent) => set({ stripePaymentIntent }),
        setPaymentSession: (paymentSession) => set({ paymentSession }),
        setLoadedFlow: (loadedFlow) => set({ loadedFlow }),
        setLoadingProviderPayment: (loadingProviderPayment) => set({ loadingProviderPayment }),

        dispose: () => {
            // First: every in-flight request's callbacks become inert, so none of them
            // can re-populate the fields this reset is about to clear.
            flowGeneration += 1;
            // Tear down any mounted Checkout.com flow component, then reset every
            // flow field to its default so no token / session / status carries into
            // the next login (formerly PaymentsStore.dispose + clearBraintreeData).
            try {
                get().flowComponent?.unmount();
            } catch {
                // Checkout may already have destroyed the component on logout.
            }
            set({ ...INITIAL_FLOW_STATE });
        },

        createStripePaymentIntent: async (
            userId,
            amountInDollars,
            onSuc = noop,
            onErr = noop,
            debitCardId,
            isCashApp,
            destination,
        ) => {
            const generation = flowGeneration;
            const isDeposit = amountInDollars > 0;
            if (isDeposit) {
                metrics.recordDepositMetric({
                    step: debitCardId ? 'submitCardInstrumentInit' : 'newCardCreateTokenInit',
                });
            }

            set({ loading: true });
            const amountInCents = Math.round(amountInDollars * CENTS_PER_DOLLAR);
            const sardine = await fetchSardineSessionKey('deposit', userId);
            // Not `ok`: the session that captured `userId` above has ended. Issuing the
            // request now sends the ended user's operation, and its 401 is read against
            // whoever is signed in by then.
            if (!sardine.ok || generation !== flowGeneration) return;
            const sardineSessionKey = sardine.key;

            const { isApplePayOnly, isGooglePayOnly } = usePaymentsWalletStore.getState();
            const cardWalletType = getCardWalletType(isApplePayOnly, isGooglePayOnly);
            const payload: StripePaymentIntentRequest = {
                amount_cents: amountInCents,
                riskified_cart_token: getRiskifiedSessionId() || undefined,
                sardine_session_key: sardineSessionKey || undefined,
                card_wallet_type: cardWalletType,
                destination: isDeposit ? destination : undefined,
                // Every deposit on /stripe/payment_intent opts into partial
                // authorization: a saved card is authorized inside the request and
                // reports the short amount in the response, and a card entered in the
                // payment sheet reports it on the intent the confirm/3DS step leaves
                // behind. The two exclusions are the flows this same action serves
                // that are not that endpoint or not a deposit: Cash App (posts to
                // /stripe/cashapp_payment_intent) and withdrawals (negative amount).
                supports_partial_authorization: isDeposit && !isCashApp ? true : undefined,
                ...(isDeposit ? getDepositRequestTrackingProps() : {}),
            };
            if (debitCardId) {
                payload.debit_card_id = debitCardId;
            }

            const onSuccess = (paymentIntent: StripePaymentIntent) => {
                if (generation !== flowGeneration) return;
                set({ stripePaymentIntent: paymentIntent });
                onSuc();
                if (isDeposit) {
                    metrics.recordDepositMetric({
                        step: debitCardId ? 'submitCardInstrumentSuccess' : 'newCardCreateTokenSuccess',
                        transfer_id: paymentIntent.transfer_id,
                        payment_intent_id: paymentIntent.payment_intent_id,
                        deposit_id: paymentIntent.deposit_id,
                    });
                }
            };

            const onError = (error: ServerError) => {
                if (generation !== flowGeneration) return;
                if (isDeposit) {
                    metrics.recordDepositMetric({
                        step: debitCardId ? 'submitCardInstrumentError' : 'newCardCreateTokenError',
                    });
                }
                onErr(error);
            };

            const onFinally = () => {
                if (generation !== flowGeneration) return;
                set({ loading: false });
            };

            if (isCashApp) {
                createStripeCashappPaymentIntentRequest(userId, payload, onSuccess, onError, onFinally);
            } else {
                createStripePaymentIntentRequest(userId, payload, onSuccess, onError, onFinally);
            }
        },

        createStripeANIDeposit: async (
            userId,
            amountInDollars,
            instrument,
            onSuc = noop,
            onErr = noop,
            destination,
        ) => {
            const generation = flowGeneration;
            metrics.recordDepositMetric({ step: 'newCardCreateTokenInit' });

            set({ loading: true });
            const amountInCents = Math.round(amountInDollars * CENTS_PER_DOLLAR);
            const sardine = await fetchSardineSessionKey('deposit', userId);
            if (!sardine.ok || generation !== flowGeneration) return;
            const sardineSessionKey = sardine.key;

            createStripeANIDepositRequest(
                userId,
                {
                    amount_cents: amountInCents,
                    ...instrument,
                    riskified_cart_token: getRiskifiedSessionId() || undefined,
                    sardine_session_key: sardineSessionKey || undefined,
                    destination,
                    // The server confirms this deposit itself, so it can hold a
                    // partially authorized amount and let the client accept or
                    // release it rather than declining outright.
                    supports_partial_authorization: true,
                    ...getDepositRequestTrackingProps(),
                },
                scopedToSession(generation, (response) => {
                    if (response.client_secret) {
                        set({ stripePaymentIntent: { client_secret: response.client_secret } });
                    }
                    metrics.recordDepositMetric({ step: 'newCardCreateTokenSuccess' });
                    onSuc(response);
                }),
                scopedToSession(generation, (error) => {
                    metrics.recordDepositMetric({ step: 'newCardCreateTokenError' });
                    onErr(error);
                }),
                scopedToSession(generation, () => {
                    set({ loading: false });
                }),
            );
        },

        createStripeCryptoPaymentIntent: async (userId, amountInDollars, context, onSuc = noop, onErr = noop) => {
            const generation = flowGeneration;
            const amountInCents = Math.round(amountInDollars * CENTS_PER_DOLLAR);
            const sardine = await fetchSardineSessionKey('deposit', userId);
            if (!sardine.ok || generation !== flowGeneration) return;
            const sardineSessionKey = sardine.key;
            createStripeCryptoPaymentIntentRequest(
                userId,
                {
                    amount_cents: amountInCents,
                    sardine_session_key: sardineSessionKey || undefined,
                    destination: context.destination,
                    deposit_flow_id: context.depositFlowId,
                    frontend_session_id: context.frontendSessionId,
                    return_url: context.returnUrl,
                },
                scopedToSession(generation, (paymentIntent) => {
                    onSuc(paymentIntent.client_secret, paymentIntent.deposit_id);
                }),
                scopedToSession(generation, (error) => {
                    onErr(error);
                }),
            );
        },

        getBraintreeClientToken: (userId, onSuc = noop, onErr = noop) => {
            const { loadedBraintreeClientToken, loadingBraintree } = get();
            if (loadedBraintreeClientToken || loadingBraintree) {
                onSuc();
                return;
            }

            const generation = flowGeneration;
            set({ loadingBraintree: true });

            getBraintreeClientTokenRequest(
                userId,
                scopedToSession(generation, (response) => {
                    set({
                        braintreeClientToken: response.client_token,
                        braintreeClientMetadataId: response.client_metadata_id,
                        loadedBraintreeClientToken: true,
                    });
                    onSuc();
                }),
                scopedToSession(generation, (err) => {
                    logProviderFactory.logError(`Failed to get Braintree client token: ${err}`);
                    onErr();
                }),
                scopedToSession(generation, () => {
                    set({ loadingBraintree: false });
                }),
            );
        },

        initiateBraintreeDeposit: async (
            userId,
            amountInDollars,
            type,
            paymentMethodNonce,
            paymentMethodId,
            deviceData,
            onSuc = noop,
            onErr = noop,
            destination,
        ) => {
            const generation = flowGeneration;
            const amountInCents = Math.round(amountInDollars * 100);

            const clientMetadataId = get().braintreeClientMetadataId;
            if (!clientMetadataId) {
                logProviderFactory.logError('Missing Braintree client metadata ID');
                return;
            }

            metrics.recordDepositMetric({
                step: 'submitCardInstrumentInit',
            });

            set({ loadingBraintree: true });
            const sardine = await fetchSardineSessionKey('deposit', userId);
            if (!sardine.ok || generation !== flowGeneration) return;
            const sardineSessionKey = sardine.key;

            const payload: InitiateBraintreeDepositRequest = {
                amount_cents: amountInCents,
                type,
                client_metadata_id: clientMetadataId,
                sardine_session_key: sardineSessionKey || undefined,
                destination,
                ...getDepositRequestTrackingProps(),
            };

            if (paymentMethodNonce) {
                payload.payment_method_nonce = paymentMethodNonce;
            }
            if (paymentMethodId) {
                payload.payment_method_id = paymentMethodId;
            }
            if (deviceData) {
                payload.device_data = deviceData;
            }

            initiateBraintreeDepositRequest(
                userId,
                payload,
                scopedToSession(generation, (response) => {
                    metrics.recordDepositMetric({
                        step: 'cardDepositSuccess',
                        transfer_id: response.transfer_id,
                    });
                    onSuc(response.transaction_id, response.transfer_id);
                }),
                scopedToSession(generation, (err) => {
                    metrics.recordDepositMetric({
                        step: 'submitCardInstrumentInit',
                    });
                    logProviderFactory.logError(`Braintree deposit error: ${JSON.stringify(err)}`);
                    onErr(err);
                }),
                scopedToSession(generation, () => {
                    set({ loadingBraintree: false });
                }),
            );
        },

        initiateBraintreeWithdrawal: async (
            userId,
            amountInCents,
            type,
            mfaCode,
            onSuc = noop,
            onErr = noop,
            source,
        ) => {
            const generation = flowGeneration;
            set({ loadingBraintree: true });
            const sardine = await fetchSardineSessionKey('withdrawal', userId);
            if (!sardine.ok || generation !== flowGeneration) return;
            const sardineSessionKey = sardine.key;

            initiateBraintreeWithdrawalRequest(
                userId,
                {
                    amount_cents: amountInCents,
                    type,
                    mfa_code: mfaCode,
                    sardine_session_key: sardineSessionKey || undefined,
                    source,
                },
                scopedToSession(generation, (response) => {
                    onSuc(response);
                }),
                scopedToSession(generation, (err) => {
                    logProviderFactory.logError(`Braintree withdrawal error: ${JSON.stringify(err)}`);
                    onErr(err);
                }),
                scopedToSession(generation, () => {
                    set({ loadingBraintree: false });
                }),
            );
        },

        clearBraintreeData: () =>
            set({
                braintreeClientToken: undefined,
                braintreeClientMetadataId: undefined,
                loadedBraintreeClientToken: false,
            }),

        debitCardWithdrawal: async (
            userId,
            amountInCents,
            mfaCode,
            paymentId,
            debitCardId,
            onSuc = noop,
            onErr = noop,
            source,
        ) => {
            const generation = flowGeneration;
            set({ loadingDebitCardWithdrawal: true });
            const sardine = await fetchSardineSessionKey('withdrawal', userId);
            if (!sardine.ok || generation !== flowGeneration) return;
            const sardineSessionKey = sardine.key;
            debitCardWithdrawalRequest(
                userId,
                {
                    amount_cents: amountInCents,
                    payment_id: paymentId,
                    debit_card_id: debitCardId,
                    mfa_code: mfaCode,
                    sardine_session_key: sardineSessionKey || undefined,
                    source,
                },
                scopedToSession(generation, (response) => {
                    onSuc(response);
                }),
                scopedToSession(generation, onErr),
                scopedToSession(generation, () => {
                    set({ loadingDebitCardWithdrawal: false, loadedDebitCardWithdrawal: true });
                }),
            );
        },

        getStripeAccountLink: (userId, returnUrl, refreshUrl, onSuc = noop, onErr = noop) => {
            if (get().loading) {
                return;
            }

            const generation = flowGeneration;
            set({ loading: true });
            getStripeAccountLinkRequest(
                userId,
                returnUrl,
                refreshUrl,
                scopedToSession(generation, (response) => {
                    onSuc(response);
                }),
                scopedToSession(generation, onErr),
                scopedToSession(generation, () => {
                    set({ loading: false });
                }),
            );
        },

        createStripeWithdrawal: async (
            userId,
            amountInCents,
            mfaCode,
            onSuc = noop,
            onErr = noop,
            source,
            payoutMethodId,
        ) => {
            const generation = flowGeneration;
            set({ loadingDebitCardWithdrawal: true });
            const sardine = await fetchSardineSessionKey('withdrawal', userId);
            if (!sardine.ok || generation !== flowGeneration) return;
            const sardineSessionKey = sardine.key;
            createStripeWithdrawalRequest(
                userId,
                {
                    amount_cents: amountInCents,
                    payout_method_id: payoutMethodId || undefined,
                    mfa_code: mfaCode,
                    sardine_session_key: sardineSessionKey || undefined,
                    source,
                },
                scopedToSession(generation, (response) => {
                    onSuc(response);
                }),
                scopedToSession(generation, onErr),
                scopedToSession(generation, () => {
                    set({ loadingDebitCardWithdrawal: false, loadedDebitCardWithdrawal: true });
                }),
            );
        },

        createStripeCryptoWithdrawal: async (
            userId,
            amountInCents,
            payoutMethodId,
            mfaCode,
            onSuc = noop,
            onErr = noop,
            source,
        ) => {
            const generation = flowGeneration;
            const sardine = await fetchSardineSessionKey('withdrawal', userId);
            if (!sardine.ok || generation !== flowGeneration) return;
            const sardineSessionKey = sardine.key;
            createStripeCryptoWithdrawalRequest(
                userId,
                {
                    amount_cents: amountInCents,
                    payout_method_id: payoutMethodId,
                    mfa_code: mfaCode,
                    sardine_session_key: sardineSessionKey || undefined,
                    source,
                },
                scopedToSession(generation, (response) => {
                    onSuc(response);
                }),
                scopedToSession(generation, onErr),
            );
        },

        tokenizedCardDeposit: async (
            userId,
            amountInDollars,
            token,
            pathname,
            addressLineOne,
            addressLineTwo,
            city,
            state,
            zipCode,
            country,
            saveCard,
            onSuc = noop,
            onErr = noop,
            destination,
        ) => {
            const generation = flowGeneration;
            set({ loadingDebitCardDeposit: true });

            const parsedAmountInDollars = Math.round(amountInDollars * CENTS_PER_DOLLAR) / CENTS_PER_DOLLAR;
            const amountInCenticents = Math.round(parsedAmountInDollars * CENTICENTS_PER_DOLLAR);

            if (parsedAmountInDollars > 0) {
                setLocalStorageWithExpiration(DEBIT_DEPOSIT_AMOUNT, parsedAmountInDollars);
            }

            const fullPath = `${config.baseUrl}${pathname}`;
            const successUrl = fullPath;
            const failureUrl = getCheckoutFailureUrl();

            // startPaymentSession already consumed the dialog's prestarted session for this
            // deposit; reuse its key so the charge scores against the session that collected
            // the card-entry behavior. Only refetch if this ran without a payment session.
            // Cleared on read so a key can never carry into a later, unrelated transfer —
            // the store outlives the dialog, which tears down via useDeposit's reset().
            const storedSardineSessionKey = get().paymentSessionSardineKey;
            if (storedSardineSessionKey) {
                set({ paymentSessionSardineKey: '' });
            }
            let sardineSessionKey = storedSardineSessionKey;
            if (!sardineSessionKey) {
                const sardine = await fetchSardineSessionKey('deposit', userId);
                if (!sardine.ok || generation !== flowGeneration) return;
                sardineSessionKey = sardine.key;
            }
            const payload = buildTokenizedDepositPayload(
                amountInCenticents,
                token,
                successUrl,
                failureUrl,
                { addressLineOne, addressLineTwo, city, state, zipCode, country, saveCard },
                destination,
                sardineSessionKey,
            );

            savedDebitCardDepositRequest(
                userId,
                payload,
                scopedToSession(generation, (response) => {
                    onSuc(response);
                }),
                scopedToSession(generation, onErr),
                scopedToSession(generation, () => {
                    set({ loadingDebitCardDeposit: false, loadedDebitCardDeposit: true });
                }),
            );
        },

        tokenizedCardWithdrawal: async (userId, amountInCents, mfaCode, token, onSuc = noop, onErr = noop, source) => {
            const generation = flowGeneration;
            set({ loadingDebitCardWithdrawal: true });
            const sardine = await fetchSardineSessionKey('withdrawal', userId);
            if (!sardine.ok || generation !== flowGeneration) return;
            const sardineSessionKey = sardine.key;
            debitCardWithdrawalRequest(
                userId,
                {
                    amount_cents: amountInCents,
                    token,
                    mfa_code: mfaCode,
                    sardine_session_key: sardineSessionKey || undefined,
                    source,
                },
                scopedToSession(generation, () => {
                    onSuc();
                }),
                scopedToSession(generation, onErr),
                scopedToSession(generation, () => {
                    set({ loadingDebitCardWithdrawal: false, loadedDebitCardWithdrawal: true });
                }),
            );
        },

        savedDebitCardDeposit: async (
            userId,
            amountInDollars,
            feeInDollars,
            debitCardId,
            pathname,
            destination,
            onSuc = noop,
            onErr = noop,
        ) => {
            const generation = flowGeneration;
            set({ loadingDebitCardDeposit: true });
            const parsedAmountInDollars = Math.round(amountInDollars * CENTS_PER_DOLLAR) / CENTS_PER_DOLLAR;

            if (parsedAmountInDollars > 0) {
                setLocalStorageWithExpiration(DEBIT_DEPOSIT_AMOUNT, parsedAmountInDollars);
            }
            if (feeInDollars > 0) {
                setLocalStorageWithExpiration(DEBIT_DEPOSIT_FEE, feeInDollars);
            }

            const amountInCenticents = Math.round(parsedAmountInDollars * CENTICENTS_PER_DOLLAR);
            const fullPath = `${config.baseUrl}${pathname}`;
            const successUrl = fullPath;
            const failureUrl = getCheckoutFailureUrl();

            const sardine = await fetchSardineSessionKey('deposit', userId);

            if (!sardine.ok || generation !== flowGeneration) return;

            const sardineSessionKey = sardine.key;
            savedDebitCardDepositRequest(
                userId,
                {
                    amount: amountInCenticents,
                    debit_card_id: debitCardId,
                    success_url: successUrl,
                    failure_url: failureUrl,
                    destination,
                    sardine_session_key: sardineSessionKey || undefined,
                    ...getDepositRequestTrackingProps(),
                },
                scopedToSession(generation, (response) => {
                    if (response?.payment_success === true) {
                        onSuc(response);
                        return;
                    }
                    const checkout3DSUrl = response['3DSlink']
                        ? getTrustedCheckout3DSUrl(response['3DSlink'])
                        : undefined;
                    if (checkout3DSUrl) {
                        if (response.payment_id) {
                            setLocalStorageWithExpiration('DEBIT_DEPOSIT_PAYMENT_ID', response.payment_id);
                        }
                        metrics.recordDepositMetric({
                            step: 'cardChallengeRequested',
                            payment_intent_id: response.payment_id,
                        });
                        metrics.recordDepositMetric({
                            step: 'cardChallengeRequestedInit',
                            payment_intent_id: response.payment_id,
                        });
                        window.location.href = checkout3DSUrl;
                        return;
                    }
                    // A rejected 3DS link leaves no provider copy to show (`payment_success` is
                    // false and the failure fields are only populated for real declines), so
                    // without a fallback the caller clears the title and message and renders an
                    // unexplained failure screen.
                    onErr(
                        undefined,
                        response.failure_title ?? CHECKOUT_3DS_FAILURE_TITLE,
                        response.failure_description ?? CHECKOUT_3DS_FAILURE_DESCRIPTION,
                    );
                }),
                scopedToSession(generation, onErr),
                scopedToSession(generation, () => {
                    set({ loadingDebitCardDeposit: false, loadedDebitCardDeposit: true });
                }),
            );
        },

        getDebitPaymentStatus: (userId, checkoutPaymentId, onSuc = noop, onErr = noop) => {
            const generation = flowGeneration;
            getDebitPaymentStatusRequest(
                userId,
                checkoutPaymentId,
                scopedToSession(generation, onSuc),
                scopedToSession(generation, onErr),
            );
        },

        startPaymentSession: async (
            userId,
            amountInDollars,
            pathname,
            addressLineOne,
            addressLineTwo = '',
            city,
            state,
            zipCode,
            country,
            saveCard,
            onSuc = noop,
            onErr = noop,
            destination,
        ) => {
            const isDeposit = amountInDollars > 0;
            if (isDeposit) {
                metrics.recordDepositMetric({ step: 'newCardCreateTokenInit' });
            }

            const generation = flowGeneration;
            set({ loading: true });
            const amountInCenticents = Math.round(amountInDollars * CENTICENTS_PER_DOLLAR);
            const fullPath = typeof window !== 'undefined' ? window.location.href : `${config.baseUrl}${pathname}`;
            const sardine = await fetchSardineSessionKey('deposit', userId);
            if (!sardine.ok || generation !== flowGeneration) return;
            const sardineSessionKey = sardine.key;
            // Hand this session to the tokenized deposit that follows, so the charge is
            // scored against the session that watched the user enter their card.
            set({ paymentSessionSardineKey: sardineSessionKey });

            startDebitCardPaymentSessionRequest(
                userId,
                {
                    amount: amountInCenticents,
                    success_url: fullPath,
                    failure_url: getCheckoutFailureUrl(),
                    address_line_one: addressLineOne,
                    address_line_two: addressLineTwo,
                    city,
                    state,
                    zip_code: zipCode,
                    country,
                    save_card: saveCard,
                    sardine_session_key: sardineSessionKey || undefined,
                    destination: isDeposit ? destination : undefined,
                    ...(isDeposit ? getDepositRequestTrackingProps() : {}),
                },
                scopedToSession(generation, (paymentSession) => {
                    set({ paymentSession });
                    onSuc();
                    if (isDeposit) {
                        metrics.recordDepositMetric({ step: 'newCardCreateTokenSuccess' });
                    }
                }),
                scopedToSession(generation, (error) => {
                    if (isDeposit) {
                        metrics.recordDepositMetric({ step: 'newCardCreateTokenError' });
                    }
                    onErr(error);
                }),
                scopedToSession(generation, () => {
                    set({ loading: false });
                }),
            );
        },

        createCheckoutFlow: async (
            operation,
            amountInDollars,
            feeInDollars,
            onReady,
            onPaymentCompleted,
            onErr,
            addressLineOne,
            addressLineTwo,
            city,
            state,
            zipCode,
            country,
            saveCard,
            _mfaCode,
            destination,
            onPaymentStarted = noop,
        ) => {
            const currentPaymentSession = get().paymentSession;
            if (!currentPaymentSession) {
                onErr();
                return;
            }

            const env = config.frontendEnv === 'production' ? 'production' : 'sandbox';

            const paymentSession: PaymentSessionResponse = {
                id: currentPaymentSession.id,
                payment_session_token: currentPaymentSession.payment_session_token,
                payment_session_secret: currentPaymentSession.payment_session_secret,
            };

            const amount = toMoneyWithCentsIfCents(amountInDollars);
            const fee = feeInDollars > 0 ? toMoneyWithCentsIfCents(feeInDollars) : undefined;
            const isDarkMode = selectIsDark(useColorPaletteOverrideStore.getState());

            // Resolves the tokenized-deposit response into the checkout callbacks
            // + deposit-dialog state (formerly PaymentsStore.handleDepositResponse).
            const handleDepositResponse = (response: SavedDebitCardDepositResponse) => {
                const depositDialog = useDepositDialogStore.getState().actions;
                const checkout3DSUrl = response['3DSlink'] ? getTrustedCheckout3DSUrl(response['3DSlink']) : undefined;
                if (response.payment_success) {
                    get().actions.setPaymentStatus('success');
                    onPaymentCompleted({
                        status: 'Approved',
                        id: response.payment_id,
                        type: 'card',
                    } as PayPaymentSessionSuccessfulResponse);
                } else if (checkout3DSUrl) {
                    if (response.payment_id) {
                        setLocalStorageWithExpiration('DEBIT_DEPOSIT_PAYMENT_ID', response.payment_id);
                    }
                    metrics.recordDepositMetric({
                        step: 'cardChallengeRequested',
                        payment_intent_id: response.payment_id,
                    });
                    metrics.recordDepositMetric({
                        step: 'cardChallengeRequestedInit',
                        payment_intent_id: response.payment_id,
                    });
                    window.location.href = checkout3DSUrl;
                } else if (response.failure_title && response.failure_description) {
                    get().actions.setPaymentStatus('failure');
                    depositDialog.setErrorTitle(response.failure_title);
                    depositDialog.setErrorMessage(response.failure_description);
                    depositDialog.setErrorCode('tokenized_card_deposit_failure');
                    depositDialog.setStep('failed');
                    depositDialog.setFee(0);
                    metrics.recordDepositMetric({ step: 'newCardCreateTokenError' });
                } else {
                    // A rejected 3DS link arrives with no provider failure copy (those fields are
                    // only populated for real declines), so supply our own rather than leaving an
                    // unexplained failure screen.
                    //
                    // `onErr` runs FIRST on purpose: `DepositCardStep`'s callback unconditionally
                    // overwrites the dialog's title, message and code (and only honours `error`
                    // for the title, never the message), so anything written before it is
                    // discarded. Doing the caller's loading/tracking cleanup first and our copy
                    // after is what makes it stick.
                    get().actions.setPaymentStatus('failure');
                    // Called bare: `CheckoutError['code']` is a closed union of provider codes, so
                    // our own code cannot travel through it. The caller's tracking therefore logs
                    // its generic code, while the dialog carries the specific one set below.
                    onErr();
                    depositDialog.setErrorTitle(CHECKOUT_3DS_FAILURE_TITLE);
                    depositDialog.setErrorMessage(CHECKOUT_3DS_FAILURE_DESCRIPTION);
                    depositDialog.setErrorCode(CHECKOUT_3DS_REJECTED_CODE);
                    depositDialog.setStep('failed');
                    depositDialog.setFee(0);
                }
            };

            const handleTokenizedDeposit = async (token: string): Promise<void> => {
                const { userId } = useAuthStore.getState();
                get().actions.tokenizedCardDeposit(
                    userId ?? '',
                    amountInDollars,
                    token,
                    window.location.pathname,
                    addressLineOne,
                    addressLineTwo,
                    city,
                    state,
                    zipCode,
                    country,
                    saveCard,
                    (response) => {
                        handleDepositResponse(response);
                    },
                    (error) => {
                        logProviderFactory.logError(`Tokenized card deposit error - ${JSON.stringify(error)}`);
                        get().actions.setPaymentStatus('failure');
                        onErr(undefined, error);
                    },
                    destination,
                );
            };

            const checkout = await loadCheckoutWebComponentsWithRetry({
                paymentSession,
                publicKey: config.checkoutPublicKey,
                environment: env,
                appearance: getCheckoutTemplate(isDarkMode),
                locale: 'en-US',
                translations: getCheckoutTranslations(operation, amount, fee),
                componentOptions: {
                    applepay: {
                        acceptedCardSchemes: [CardScheme.Visa, CardScheme.Mastercard],
                    },
                    googlepay: {
                        acceptedCardSchemes: [CardScheme.Visa, CardScheme.Mastercard],
                    },
                    card: {
                        acceptedCardSchemes: [CardScheme.Visa, CardScheme.Mastercard],
                        handleSubmit:
                            operation === 'deposit'
                                ? async (component) => {
                                      onPaymentStarted();
                                      return {
                                          id: 'tokenization-only',
                                          status: 'Pending',
                                          type: component.type,
                                      };
                                  }
                                : undefined,
                    },
                },
                onReady,
                onPaymentCompleted: (_component, paymentResponse) => {
                    if (paymentResponse.status === 'Approved') {
                        set({ paymentSession: undefined });
                        get().actions.setPaymentStatus('success');
                        onPaymentCompleted(paymentResponse);
                    } else {
                        get().actions.setPaymentStatus('failure');
                    }
                    set({ loadingProviderPayment: false });
                },
                onError: (_component, error) => {
                    get().actions.setPaymentStatus('failure');
                    set({ loadingProviderPayment: false });
                    onErr(error);
                },
                onSubmit: () => {
                    if (operation === 'deposit') {
                        onPaymentStarted();
                    }
                },
                onChange: (component) => {
                    if (['googlepay', 'applepay'].includes(component.type) && component.isValid()) {
                        set({ loadingProviderPayment: true });
                    }
                },
                onTokenized: async (_component, tokenizeResult) => {
                    if (operation !== 'deposit') {
                        return;
                    }

                    if (!tokenizeResult || !('data' in tokenizeResult)) {
                        get().actions.setPaymentStatus('failure');
                        onErr();
                        return;
                    }

                    onPaymentStarted();
                    set({ loadingProviderPayment: true });

                    try {
                        await handleTokenizedDeposit(tokenizeResult.data.token);
                    } catch (error) {
                        logProviderFactory.logError(`Handle tokenized deposit error - ${error}`);
                        get().actions.setPaymentStatus('failure');
                        set({ loadingProviderPayment: false });
                        onErr();
                    }
                },
            });

            const existingFlow = get().flowComponent;
            if (existingFlow) {
                existingFlow.unmount();
            }

            const flowComponent = checkout.create('flow');
            set({ flowComponent });
            const isAvailable = await flowComponent?.isAvailable();
            if (isAvailable) {
                flowComponent.mount('#flow-container');
            }

            // Retry-mount hack: if the flow hasn't loaded after 5s, bounce the
            // dialog step to force a reload (mirrors the old PaymentsStore hack).
            setTimeout(() => {
                if (!get().loadedFlow) {
                    useDepositDialogStore.getState().actions.setStep('billing-address');
                    setTimeout(() => {
                        useDepositDialogStore.getState().actions.setStep('debit-card');
                    }, 0);
                }
            }, 5000);
        },
    },
}));
